import React from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { makeStyles } from '@material-ui/core/styles'
import { Icon } from '@iconify/react'
import { StatusPill, colors } from 'src/components/app-ui'
import CourseToolBar from 'src/components/course-card/course-tool-bar'
import { getCourseCover } from 'src/pages/courses/components/course-library-card'
import { formatShortDate } from 'src/utils/library-sort'

const useStyles = makeStyles({
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    padding: '12px 16px',
    borderBottom: '1px solid #F1F3F5',
    background: '#FFFFFF',
    cursor: 'pointer',
    '&:hover': {
      background: '#F7F7F7',
    },
  },
  grip: {
    display: 'inline-flex',
    padding: 0,
    border: 0,
    background: 'transparent',
    color: colors.disabled,
    cursor: 'grab',
    touchAction: 'none',
  },
  step: {
    width: 26,
    height: 26,
    flex: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1.5px solid #D0D5D9',
    borderRadius: '50%',
    fontSize: '12px',
    fontWeight: 600,
    color: colors.muted,
  },
  cover: {
    width: 72,
    height: 45,
    flex: 'none',
    borderRadius: '6px',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundColor: colors.hover,
  },
  text: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontSize: '14px',
    fontWeight: 600,
    color: colors.navy,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  meta: {
    marginTop: 2,
    fontSize: '12px',
    color: colors.subtle,
  },
  status: {
    padding: '3px 10px',
    fontSize: '12px',
  },
  lock: {
    display: 'inline-flex',
    color: colors.subtle,
  },
  iconButton: {
    display: 'inline-flex',
    padding: 4,
    border: 0,
    borderRadius: '6px',
    background: 'transparent',
    cursor: 'pointer',
    color: colors.muted,
    '&:hover': {
      background: colors.primarySoft,
      color: colors.primary,
    },
  },
  icon16: {
    width: 16,
    height: 16,
  },
  actions: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  },
})

/** One sortable course row in a learning path. */
const CourseRow = ({
  course,
  step,
  locked,
  onOpen,
  onEdit,
  localCourses,
  setLocalCourses,
}) => {
  const classes = useStyles()
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: course.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    position: 'relative',
    zIndex: isDragging ? 2 : 'auto',
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={classes.row}
      title="Open course"
      onClick={onOpen}
    >
      <button
        type="button"
        className={classes.grip}
        title="Drag to reorder"
        onClick={event => event.stopPropagation()}
        {...attributes}
        {...listeners}
      >
        <Icon icon="tabler:grip-vertical" width={18} height={18} />
      </button>
      <span className={classes.step}>{step}</span>
      <div
        className={classes.cover}
        style={{ backgroundImage: `url(${getCourseCover(course.thumbnail)})` }}
      />
      <div className={classes.text}>
        <div className={classes.title}>{course.name}</div>
        <div className={classes.meta}>
          Updated {formatShortDate(course.updatedAt || course.createdAt)}
        </div>
      </div>
      <StatusPill
        status={course.isPublished === false ? 'Draft' : 'Published'}
        className={classes.status}
      />
      {locked && (
        <span className={classes.lock} title="Unlocks after previous course">
          <Icon icon="tabler:lock" className={classes.icon16} />
        </span>
      )}
      <span
        className={classes.actions}
        onClick={event => event.stopPropagation()}
      >
        <button
          type="button"
          className={classes.iconButton}
          title="Edit in editor"
          onClick={onEdit}
        >
          <Icon icon="tabler:pencil" className={classes.icon16} />
        </button>
        <CourseToolBar
          course={course}
          localCourses={localCourses}
          setLocalCourses={setLocalCourses}
          renderTrigger={({ openMenu }) => (
            <button
              type="button"
              className={classes.iconButton}
              title="More"
              onClick={openMenu}
            >
              <Icon icon="tabler:dots-vertical" className={classes.icon16} />
            </button>
          )}
        />
      </span>
    </div>
  )
}

export default CourseRow
